let _id = 1;
const uid = (prefix = "f") => `${prefix}-${_id++}-${Date.now()}`;

const field = (label, type, required = false, extras = {}) => {
  const mapsTo = extras.mapsTo || extras.maps_to || "custom";
  const fieldUid =
    extras.fieldUid ||
    extras.field_uid ||
    (mapsTo !== "custom" && mapsTo !== "section" ? mapsTo : uid("fld"));
  const {
    mapsTo: _a,
    maps_to: _b,
    fieldUid: _c,
    field_uid: _d,
    ...rest
  } = extras;
  return {
    id: fieldUid,
    fieldUid,
    mapsTo,
    label,
    fieldType: type,
    required,
    placeholder: "",
    helpText: "",
    defaultValue: "",
    options: [],
    validation: {},
    enabled: true,
    ...rest,
  };
};

const opts = (arr) => arr.map((o) => ({ label: o, value: o }));

export const LOCKED_FIELD_MAPS = [
  "customer.phone",
  "customer.services_interested_in",
];

// Sections whose disable toggle is always ON (cannot be turned off)
export const ALWAYS_ENABLED_SECTIONS = [
  "customer / pet parent information",
  "customer address",
  "pet basic information",
];

// Fields whose disable toggle is always ON (cannot be turned off)
export const ALWAYS_ENABLED_FIELD_LABELS = [
  "pet name",
  "pet type",
  "breed",
  "size",
];

export const isAlwaysEnabledSection = (section) => {
  if (!section) return false;
  const name = String(section.sectionName || "")
    .trim()
    .toLowerCase();
  return ALWAYS_ENABLED_SECTIONS.includes(name);
};

export const isAlwaysEnabledField = (f) => {
  if (!f) return false;
  const label = String(f.label || "")
    .trim()
    .toLowerCase();
  return ALWAYS_ENABLED_FIELD_LABELS.includes(label);
};

export const isLockedFormField = (f) => {
  if (!f) return false;

  if (isAlwaysEnabledField(f)) return true;

  if (f.locked === true || f.disableAllowed === false) {
    const mapsTo = f.mapsTo || f.maps_to;
    if (LOCKED_FIELD_MAPS.includes(mapsTo)) return true;
  }
  const mapsTo = f.mapsTo || f.maps_to;
  if (LOCKED_FIELD_MAPS.includes(mapsTo)) return true;
  const label = String(f.label || "")
    .trim()
    .toLowerCase();
  return label === "phone number" || label === "services interested in";
};

export const isCommonBasicSection = (section) => {
  if (!section) return false;
  const name = String(section.sectionName || "")
    .trim()
    .toLowerCase();
  return (
    name.includes("customer / pet parent information") ||
    name.includes("customer address") ||
    name.includes("pet basic information") ||
    name === "required details"
  );
};

export const getSectionServices = (section, fallbackServices = []) => {
  const fallback = Array.isArray(fallbackServices)
    ? fallbackServices.map((s) => String(s).trim()).filter(Boolean)
    : typeof fallbackServices === "string" && fallbackServices.trim()
      ? fallbackServices
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  if (!section) return fallback;
  if (
    Array.isArray(section.associated_services) &&
    section.associated_services.length > 0
  ) {
    return section.associated_services
      .map((s) => String(s).trim())
      .filter(Boolean);
  }
  if (
    Array.isArray(section.associatedServices) &&
    section.associatedServices.length > 0
  ) {
    return section.associatedServices
      .map((s) => String(s).trim())
      .filter(Boolean);
  }
  if (Array.isArray(section.services) && section.services.length > 0) {
    return section.services.map((s) => String(s).trim()).filter(Boolean);
  }
  if (
    typeof section.associatedService === "string" &&
    section.associatedService.trim()
  ) {
    return section.associatedService
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return fallback;
};

export const SERVICE_INTEREST_OPTIONS = [
  "Grooming / Home Grooming",
  "Boarding / Day care",
  "Veterinary",
  "Others",
];

export const getCustomFormStarterSections = () => {
  _id = 1;
  const ts = Date.now();
  return [
    {
      id: `sec-required-${ts}`,
      sectionName: "Required details",
      sectionDescription:
        "Phone and services are required system fields and cannot be edited.",
      displayOrder: 1,
      collapsed: false,
      fields: [
        field("Phone Number", "phone", true, {
          mapsTo: "customer.phone",
          locked: true,
          disableAllowed: false,
          validation: {
            minLength: 10,
            maxLength: 10,
            pattern: "^[6-9][0-9]{9}$",
          },
        }),
        field("Services Interested In", "multiple_choice", true, {
          mapsTo: "customer.services_interested_in",
          locked: true,
          disableAllowed: false,
          options: opts(SERVICE_INTEREST_OPTIONS),
        }),
      ],
    },
    {
      id: `sec-new-${ts}`,
      sectionName: "Section 1",
      sectionDescription: "",
      displayOrder: 2,
      collapsed: false,
      fields: [],
    },
  ];
};

// ─── Backend Master Data (Breeds & Vaccines) ───────────────────────────────

/**
 * Breeds matching backend PetBreedSeed & /petBreeds/getPetBreeds
 */
export const SEEDED_BREEDS = [
  // Common / Indian Dog Breeds
  "Indie / Indian Pariah Dog",
  "Indian Spitz",
  "Rajapalayam",
  "Kombai",
  "Mudhol Hound",
  "Rampur Greyhound",
  "Gaddi Kutta",
  "Bakharwal Dog",
  "Chippiparai",
  // Popular Dogs
  "Labrador Retriever",
  "Golden Retriever",
  "German Shepherd",
  "Beagle",
  "Pug",
  "Dachshund",
  "Shih Tzu",
  "Rottweiler",
  "Doberman",
  "Boxer",
  "Bulldog",
  "French Bulldog",
  "Pomeranian",
  "Poodle",
  "Siberian Husky",
  "Great Dane",
  "Cocker Spaniel",
  "Maltese",
  "Yorkshire Terrier",
  "Chihuahua",
  "Border Collie",
  "Australian Shepherd",
  "Dalmatian",
  "Basset Hound",
  "Mastiff",
  "Corgi",
  "Lhasa Apso",
  "Pitbull / American Bully",
  // Popular Cats
  "Persian",
  "Siamese",
  "Maine Coon",
  "Bengal",
  "British Shorthair",
  "Ragdoll",
  "Russian Blue",
  "Sphynx",
  "Abyssinian",
  "Scottish Fold",
  "American Shorthair",
  "American Bobtail",
  "American Curl",
  "Chartreux",
  "Cornish Rex",
  "Devon Rex",
  "Egyptian Mau",
  "Exotic Shorthair",
  "Norwegian Forest Cat",
  "Savannah",
  "Siberian",
  "Singapura",
  "Turkish Van",
  "Oriental Shorthair",
  "Bombay Cat",
  "Tonkinese",
  "Burmese",
  "Oriental Bicolor",
  "Indie / Domestic Shorthair",
  // Common & Fallback
  "Mixed Breed",
  "Other",
];

/**
 * Vaccines matching backend VendorPet enums, ConsentForm questions, and client pet sections
 */
export const ALL_VACCINES = [
  "Rabies",
  "DHPP (Distemper, Hepatitis, Parainfluenza, Parvovirus)",
  "DHPPi (7-in-1 / 9-in-1)",
  "Bordetella (Kennel Cough)",
  "Parvovirus",
  "Hepatitis",
  "Distemper",
  "Parainfluenza",
  "Leptospirosis",
  "Coronavirus (Corona)",
  "Canine Influenza",
  "Lyme Disease",
  "FVRCP / Tricat (Cat Flu)",
  "Panleukopenia",
  "Calicivirus",
  "Rhinotracheitis",
  "Feline Leukemia (FeLV)",
  "Feline Chlamydia",
  "Deworming",
  "Other",
];

export const VACCINATION_STATUS_OPTIONS = [
  "Vaccinated",
  "Not Vaccinated",
  "Partially Vaccinated",
  "Unknown",
];

export const NEUTERED_STATUS_OPTIONS = [
  "Not Neutered",
  "Neutered",
  "Unknown",
];

// ─── Template ───────────────────────────────────────────────────────────────

export const getBoardingFormTemplate = (customBreeds = null) => {
  // Reset counter for deterministic ids per call
  _id = 1;

  const breedOptions =
    Array.isArray(customBreeds) && customBreeds.length > 0
      ? Array.from(new Set([...customBreeds, ...SEEDED_BREEDS]))
      : SEEDED_BREEDS;

  return {
    formName: "Customer Onboarding Form",
    formDescription:
      "Complete onboarding registration form for pet parents checking in their pets.",
    formCategory: "Boarding",
    formType: "predefined",
    status: "draft",
    sections: [
      // ── Section 1: Customer / Pet Parent Information ───────────────────────
      {
        id: "sec_customer_info",
        sectionKey: "customer_info",
        sectionName: "Customer / Pet Parent Information",
        sectionDescription: "Basic contact details of the pet owner.",
        displayOrder: 1,
        collapsed: false,
        locked: true,
        disableAllowed: false,
        fields: [
          field("First Name", "short_text", true, {
            mapsTo: "customer.firstname",
          }),
          field("Last Name", "short_text", true, {
            mapsTo: "customer.lastname",
          }),
          field("Phone Number", "phone", true, {
            mapsTo: "customer.phone",
            locked: true,
            disableAllowed: false,
            validation: {
              minLength: 10,
              maxLength: 10,
              pattern: "^[6-9][0-9]{9}$",
            },
          }),
          field("WhatsApp Number", "phone", false, {
            mapsTo: "customer.whatsapp_number",
          }),
          field("Alternate Phone Number", "phone", false, {
            mapsTo: "customer.alternate_phone",
          }),
          field("Email Address", "email", false, { mapsTo: "customer.email" }),
          field("ID Proof Document", "file_upload", true, {
            mapsTo: "customer.id_proof",
            validation: { accept: ".pdf,.jpg,.jpeg,.png", maxSizeMB: 10 },
          }),
        ],
      },

      // ── Section 2: Customer Address ───────────────────────────────────────
      {
        id: "sec_customer_address",
        sectionKey: "customer_address",
        sectionName: "Customer Address",
        sectionDescription: "Pet parent's physical address.",
        displayOrder: 2,
        collapsed: false,
        locked: true,
        disableAllowed: false,
        fields: [
          field("Home Address", "long_text", false, {
            mapsTo: "customer.address.address1",
          }),
          field("Landmark", "short_text", false, {
            mapsTo: "customer.address.landmark",
          }),
          field("City", "short_text", false, {
            mapsTo: "customer.address.city",
          }),
          field("State", "short_text", false, {
            mapsTo: "customer.address.state",
          }),
          field("Pincode", "number", false, {
            mapsTo: "customer.address.zip",
            validation: {
              minLength: 6,
              maxLength: 6,
              pattern: "^[1-9][0-9]{5}$",
            },
          }),
          field("Google Maps URL", "url", false, {
            mapsTo: "customer.address.maps_url",
            placeholder: "https://maps.google.com/...",
          }),
          field("Services Interested In", "multiple_choice", true, {
            mapsTo: "customer.services_interested_in",
            locked: true,
            disableAllowed: false,
            options: opts([
              "Grooming / Home Grooming",
              "Boarding / Day care",
              "Veterinary",
              "Others",
            ]),
          }),
        ],
      },

      // ── Section 3: Pet Basic Information ──────────────────────────────────
      {
        id: "sec_pet_basic",
        sectionKey: "pet_basic",
        sectionName: "Pet Basic Information",
        sectionDescription: "Core details about the pet being boarded.",
        displayOrder: 3,
        collapsed: false,
        locked: true,
        disableAllowed: false,
        fields: [
          field("Pet Profile Picture", "image_upload", false, {
            mapsTo: "pet.photo",
          }),
          field("Pet Name", "short_text", true, {
            mapsTo: "pet.name",
            locked: true,
            disableAllowed: false,
          }),
          field("Pet Type", "single_choice", true, {
            mapsTo: "pet.pet_type",
            locked: true,
            disableAllowed: false,
            options: opts(["Dog", "Cat", "Other"]),
          }),
          field("Breed", "dropdown", true, {
            mapsTo: "pet.breed",
            locked: true,
            disableAllowed: false,
            searchable: true,
            options: opts(breedOptions),
          }),
          field("Size", "dropdown", true, {
            mapsTo: "pet.size",
            locked: true,
            disableAllowed: false,
            options: opts(["Small", "Medium", "Large", "Extra Large"]),
          }),
          field("Gender", "single_choice", false, {
            mapsTo: "pet.gender",
            options: opts(["Male", "Female"]),
          }),
          field("Date of Birth", "date_picker", false, {
            mapsTo: "pet.birthday",
          }),
          field("Age", "short_text", false, { mapsTo: "pet.age" }),
          field("Weight (kg)", "number", false, { mapsTo: "pet.weight_kg" }),
          field("Neutered / Spayed Status", "dropdown", false, {
            mapsTo: "pet.neutered_status",
            placeholder: "Select Neutered Status",
            options: opts(NEUTERED_STATUS_OPTIONS),
          }),
        ],
      },

      // ── Section 4: Health and Medical History ─────────────────────────────
      {
        id: "sec_health_medical",
        sectionKey: "health_medical",
        sectionName: "Health and Medical History",
        sectionDescription: "Known conditions and medical background.",
        displayOrder: 4,
        collapsed: false,
        fields: [
          field(
            "Does your pet have any medical condition?",
            "single_choice",
            false,
            {
              mapsTo: "pet.has_medical_condition",
              options: opts(["Yes", "No"]),
              gatesSection: true,
              sectionDisableTitle:
                "Disable entire Health and Medical History section?",
              sectionDisableMessage:
                "Other fields in this section depend on this question. If you disable it, all remaining fields in the section will be disabled too.",
              dependentFieldLabels: ["Medical Conditions"],
            },
          ),
          field("Medical Conditions", "multiple_choice", false, {
            mapsTo: "pet.medical_conditions",
            searchable: true,
            options: opts([
              "Allergies ",
              "Anxiety",
              "Arthritis",
              "Chronic Pain",
              "Constipation",
              "Dental Disease",
              "Diabetes",
              "Diarrhea",
              "Ear Infection",
              "Ear Mites",
              "Eye Infection",
              "Flea Allergy",
              "Heart Disease",
              "Hypothyroidism",
              "Incotinence",
              "Kennel Cough",
              "Kideney Disease",
              "Limping",
              "Liver Disease",
              "Mobility Issues",
              "Pancreatitis",
              "Seizures",
              "Cancer",
              "Skin Condition",
              "Tick Fever",
              "Urinary Tract Infection",
              "Anemia",
              // "Other",
            ]),
          }),
          // field("Custom Medical Condition", "short_text", false, {
          //   mapsTo: "pet.custom_medical_condition",
          // }),
        ],
      },

      // ── Section 5: Medication Details ─────────────────────────────────────
      {
        id: "sec_medications",
        sectionKey: "medications",
        sectionName: "Medication Details",
        sectionDescription: "Current medications and dosage instructions.",
        // associated_services: ["Boarding"],
        // associatedServices: ["Boarding"],
        // associatedService: "Boarding",
        displayOrder: 5,
        collapsed: false,
        fields: [
          field("Is your pet on any medication?", "single_choice", false, {
            mapsTo: "pet.on_medication",
            options: opts(["Yes", "No"]),
            gatesSection: true,
            sectionDisableTitle: "Disable entire Medication Details section?",
            sectionDisableMessage:
              "Other fields in this section depend on this question. If you disable it, all remaining fields in the section will be disabled too.",
            dependentFieldLabels: [
              "Medication Name",
              "When to give?",
              "How much to give (dosage)",
              "Medication Instructions",
              "Medication Photo",
            ],
          }),
          field("Medication Name", "short_text", false, {
            mapsTo: "pet.medication_name",
          }),
          field("When to give?", "multiple_choice", false, {
            mapsTo: "pet.medication_time",
            options: opts(["Morning", "Afternoon", "Evening"]),
          }),
          field("How much to give (dosage)", "short_text", false, {
            mapsTo: "pet.medication_dosage",
          }),
          field("Medication Instructions", "long_text", false, {
            mapsTo: "pet.medication_instructions",
          }),
          field("Medication Photo", "image_upload", false, {
            mapsTo: "pet.medication_photo",
          }),
        ],
      },

      // ── Section 6: Vaccination Details ────────────────────────────────────
      {
        id: "sec_vaccination",
        sectionKey: "vaccination",
        sectionName: "Vaccination Details",
        sectionDescription: "Rabies and DHPPi vaccination records.",
        // associated_services: ["Boarding"],
        // associatedServices: ["Boarding"],
        // associatedService: "Boarding",
        displayOrder: 6,
        collapsed: false,
        fields: [
          field("Rabies Vaccination Date", "date_picker", true, {
            mapsTo: "pet.rabies_vaccination_date",
          }),
          field("Rabies Vaccination Image", "image_upload", true, {
            mapsTo: "pet.rabies_vaccination_image",
          }),
          field("DHPPi Vaccination Date", "date_picker", true, {
            mapsTo: "pet.dhppi_vaccination_date",
          }),
          field("DHPPi Vaccination Image", "image_upload", true, {
            mapsTo: "pet.dhppi_vaccination_image",
          }),
        ],
      },

      // ── Section 7: Diet and Meal Instructions ─────────────────────────────
      {
        id: "sec_feeding",
        sectionKey: "feeding",
        sectionName: "Diet and Meal Instructions",
        sectionDescription: "Feeding schedule and dietary preferences.",
        // associated_services: ["Boarding"],
        // associatedServices: ["Boarding"],
        // associatedService: "Boarding",
        displayOrder: 7,
        collapsed: false,
        fields: [
          field("Meal Type", "dropdown", false, {
            mapsTo: "pet.meal_type",
            options: opts([
              "Vegetarian",
              "Non-Vegetarian",
              "Home-Cooked Diet",
              "Mixed Diet",
              "Prescription Diet",
              "Raw Diet",
            ]),
          }),
          field("Meal per day", "number", false, {
            mapsTo: "pet.meals_per_day",
          }),
          field("What to Feed?", "long_text", false, {
            mapsTo: "pet.what_to_feed",
          }),
          field("When to feed?", "dropdown", false, {
            mapsTo: "pet.when_to_feed",
            options: opts(["Morning", "Afternoon", "Evening"]),
          }),
          field("How much to Feed?", "short_text", false, {
            mapsTo: "pet.how_much_to_feed",
          }),
          field("Feeding Instructions", "long_text", false, {
            mapsTo: "pet.feeding_instructions",
          }),
        ],
      },

      // ── Section 8: Pet Behaviour ──────────────────────────────────────────
      {
        id: "sec_pet_behaviour",
        sectionKey: "pet_behaviour",
        sectionName: "Pet Behaviour",
        sectionDescription: "Behavioral traits and social tendencies.",
        displayOrder: 8,
        collapsed: false,
        fields: [
          field("Pet Behaviour", "multiple_choice", false, {
            mapsTo: "pet.behaviour",
            options: opts([
              "Friendly with Dogs",
              "Friendly with Cats",
              "Friendly with Humans",
              "Aggressive / Protective",
              "Anxious / Shy",
              "Barker / Vocal",
              "Playful",
              "Leash Reactive",
              "Resource Guarder",
              "Other",
            ]),
          }),
        ],
      },

      // ── Section 9: Pet Appearance ─────────────────────────────────────────
      {
        id: "sec_pet_appearance",
        sectionKey: "pet_appearance",
        sectionName: "Pet Appearance",
        sectionDescription: "Physical appearance details.",
        displayOrder: 9,
        collapsed: false,
        fields: [
          field("Hair Type", "dropdown", false, {
            mapsTo: "pet.hair_type",
            options: opts([
              "Smooth",
              "Curly",
              "Wire",
              "Wavy",
              "Double",
              "Drop Coat",
              "Hairless",
            ]),
          }),
          field("Hair Length", "dropdown", false, {
            mapsTo: "pet.hair_length",
            options: opts(["Short Hair", "Medium Hair", "Long Hair"]),
          }),
          field("Hair Color", "multiple_choice", false, {
            mapsTo: "pet.hair_color",
            searchable: true,
            options: opts([
              "Black",
              "White",
              "Grey ",
              "Golden",
              "Brown ",
              "Cream ",
              "Beige",
              "Other",
            ]),
          }),
        ],
      },
    ],
  };
};

const cloneSections = (sections) =>
  (sections || []).map((s, i) => ({
    ...s,
    displayOrder: i + 1,
    fields: (s.fields || []).map((f) => ({ ...f })),
  }));

const pickSections = (full, names) =>
  cloneSections(full.sections.filter((s) => names.includes(s.sectionName)));

/** First-time client + pet registration. */
export const getOnboardingFormTemplate = (customBreeds = null) => {
  const full = getBoardingFormTemplate(customBreeds);
  const sections = pickSections(full, [
    "Customer / Pet Parent Information",
    "Customer Address",
    "Pet Basic Information",
    "Health and Medical History",
    "Medication Details",
    "Vaccination Details",
    "Diet and Meal Instructions",
    "Pet Behaviour",
    "Pet Appearance",
  ]).map((s) => {
    if (s.sectionName === "Customer Address") {
      return {
        ...s,
        fields: (s.fields || []).filter((f) => f.label !== "Google Maps URL"),
      };
    }
    if (s.sectionName.startsWith("Medication Details")) {
      return {
        ...s,
        sectionName: "Medication Details",
        sectionDescription: "Current medications and dosage instructions.",
      };
    }
    if (s.sectionName === "Pet Basic Information") {
      return {
        ...s,
        sectionDescription: "Core details about the pet.",
      };
    }
    if (s.sectionName === "Health and Medical History") {
      return {
        ...s,
        fields: (s.fields || []).filter(
          (f) => f.label !== "Custom Medical Condition",
        ),
      };
    }
    return s;
  });

  return {
    formName: "Customer Onboarding Form",
    formDescription:
      "Register a new pet parent and their pets — contact, address, pet basics, health, diet, behaviour, and appearance.",
    formCategory: "Boarding",
    formType: "predefined",
    status: "draft",
    services: ["Boarding"],
    sections,
  };
};

/** Grooming-focused intake: owner, pet, coat notes, documents, consent. */
export const getGroomingFormTemplate = (customBreeds = null) => {
  const full = getBoardingFormTemplate(customBreeds);
  const base = pickSections(full, [
    "Customer / Pet Parent Information",
    "Customer Address",
    "Pet Basic Information",
    "Pet Documents",
    "Consent and Boarding Declaration",
  ]);

  const petIdx = base.findIndex(
    (s) => s.sectionName === "Pet Basic Information",
  );
  const appearance = {
    id: uid("sec"),
    sectionName: "Coat and Grooming Notes",
    sectionDescription: "Coat type and grooming preferences.",
    displayOrder: 0,
    collapsed: false,
    fields: [
      field("Hair Type", "dropdown", false, {
        mapsTo: "pet.hair_type",
        options: opts([
          "Smooth",
          "Curly",
          "Wire",
          "Wavy",
          "Double",
          "Drop Coat",
          "Hairless",
        ]),
      }),
      field("Hair Length", "dropdown", false, {
        mapsTo: "pet.hair_length",
        options: opts(["Short Hair", "Medium Hair", "Long Hair"]),
      }),
      field("Special Grooming Instructions", "long_text", false, {
        mapsTo: "pet.special_care_instructions",
        placeholder: "Style, sensitive areas, do-not-shave notes…",
      }),
    ],
  };

  const insertAt = petIdx === -1 ? base.length : petIdx + 1;
  const sections = [
    ...base.slice(0, insertAt),
    appearance,
    ...base.slice(insertAt),
  ].map((s, i) => ({
    ...s,
    displayOrder: i + 1,
    sectionName:
      s.sectionName === "Consent and Boarding Declaration"
        ? "Grooming Consent"
        : s.sectionName,
  }));

  return {
    formName: "Grooming Intake Form",
    formDescription:
      "Grooming check-in form with pet parent details, coat notes, documents, and consent.",
    formCategory: "grooming",
    formType: "predefined",
    status: "draft",
    services: ["grooming"],
    sections,
  };
};

export const FORM_STARTER_TEMPLATES = [
  {
    id: "boarding",
    title: "Customer Boarding Form",
    description:
      "Full boarding registration — owner, pet, medical, vaccination, diet, documents, and consent.",
    tags: ["Customer", "Pet", "Medical", "Diet", "Consent"],
    getTemplate: getBoardingFormTemplate,
    recommended: true,
  },
  {
    id: "onboarding",
    title: "Customer Onboarding Form",
    description:
      "First-time client setup — contact details, address, pet basics, and a short consent.",
    tags: ["Customer", "Pet", "Consent"],
    getTemplate: getOnboardingFormTemplate,
  },
  {
    id: "grooming",
    title: "Grooming Intake Form",
    description:
      "Grooming visit intake — owner, pet, coat notes, documents, and grooming consent.",
    tags: ["Customer", "Pet", "Coat", "Consent"],
    getTemplate: getGroomingFormTemplate,
  },
];

// ─── Field Type Registry ────────────────────────────────────────────────────

export const FIELD_TYPES = [
  // Text & Contact
  {
    group: "Text & Contact",
    value: "short_text",
    label: "Short Text",
    icon: "Aa",
  },
  {
    group: "Text & Contact",
    value: "long_text",
    label: "Long Text / Textarea",
    icon: "¶",
  },
  { group: "Text & Contact", value: "email", label: "Email", icon: "@" },
  { group: "Text & Contact", value: "phone", label: "Phone Number", icon: "☎" },
  { group: "Text & Contact", value: "number", label: "Number", icon: "#" },
  { group: "Text & Contact", value: "url", label: "URL", icon: "🔗" },
  // Choice
  { group: "Choice", value: "dropdown", label: "Dropdown", icon: "▾" },
  {
    group: "Choice",
    value: "single_choice",
    label: "Single Choice",
    icon: "◉",
  },
  {
    group: "Choice",
    value: "multiple_choice",
    label: "Multiple Choice",
    icon: "☑",
  },
  { group: "Choice", value: "checkbox", label: "Checkbox", icon: "✔" },
  { group: "Choice", value: "yes_no", label: "Yes / No", icon: "⊙" },
  // Date & Time
  {
    group: "Date & Time",
    value: "date_picker",
    label: "Date Picker",
    icon: "📅",
  },
  {
    group: "Date & Time",
    value: "time_picker",
    label: "Time Picker",
    icon: "⏰",
  },
  {
    group: "Date & Time",
    value: "datetime_picker",
    label: "Date & Time Picker",
    icon: "📆",
  },
  // Upload
  { group: "Upload", value: "file_upload", label: "File Upload", icon: "📎" },
  { group: "Upload", value: "image_upload", label: "Image Upload", icon: "🖼" },
  {
    group: "Upload",
    value: "multi_file_upload",
    label: "Multiple File Upload",
    icon: "📁",
  },
  // Special
  {
    group: "Special",
    value: "vaccination_details",
    label: "Vaccination Checklist",
    icon: "💉",
  },
  { group: "Special", value: "address", label: "Address", icon: "🏠" },
  { group: "Special", value: "signature", label: "Signature", icon: "✍" },
  {
    group: "Special",
    value: "section_heading",
    label: "Section Heading",
    icon: "H",
  },
  {
    group: "Special",
    value: "description_text",
    label: "Description Text",
    icon: "📝",
  },
];

export const FIELD_TYPE_MAP = Object.fromEntries(
  FIELD_TYPES.map((ft) => [ft.value, ft]),
);

export const FORM_CATEGORIES = ["Boarding", "Grooming", "Veterinary", "Other"];
// ─── Plan-based filtering helpers ────────────────────────────────────────────

const PLAN_NAME_TO_KEY = {
  grooming: "grooming",
  "grooming salon": "grooming",
  "mobile pet grooming van": "grooming",
  "kennel boarding": "boarding",
  boarding: "boarding",
  "day care services": "boarding",
  "pet resort": "boarding",
  "pet vet clinic": "vet",
  veterinary: "vet",
  "pet shop": "other",
  "dog walking": "other",
  others: "other",
};

export const getPlanServiceKeys = (profileData) => {
  const cats = profileData?.subscriptionPlan?.[0]?.plan_category || [];
  if (!cats.length) return ["grooming", "boarding", "vet", "other"];
  const keys = new Set();
  cats.forEach((c) => {
    const k =
      PLAN_NAME_TO_KEY[
        String(c.service_category_name || "")
          .toLowerCase()
          .trim()
      ];
    if (k) keys.add(k);
  });
  return Array.from(keys);
};

const KEY_TO_FORM_CATEGORY = {
  grooming: "Grooming",
  boarding: "Boarding",
  vet: "Veterinary",
  other: "Other",
};

export const getPlanFormCategories = (profileData) =>
  getPlanServiceKeys(profileData)
    .map((k) => KEY_TO_FORM_CATEGORY[k])
    .filter((cat) => Boolean(cat) && cat !== "Other"); 
const KEY_TO_INTEREST_LABEL = {
  grooming: "Grooming / Home Grooming",
  boarding: "Boarding / Day care",
  vet: "Veterinary",
  other: "Others",
};

export const getPlanServiceInterestOptions = (profileData) =>
  getPlanServiceKeys(profileData)
    .map((k) => KEY_TO_INTEREST_LABEL[k])
    .filter((opt) => Boolean(opt) && opt !== "Others");